import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { SamplingEquipment } from 'src/app/models/samplingEquipment.model';
import { DateFormField, HierarchicalSelectionFormField, SingleSelectionFormField, TextFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { SamplingEquipmentService } from 'src/app/services/sampling-equipment.service';
import { TypeService } from 'src/app/services/type.service';
import { EquipmentStatus } from 'src/app/enums/equipmentStatus.enum';
import { finalize, forkJoin } from 'rxjs';
import { SensorService } from 'src/app/services/sensor.service';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';

@Component({
  standalone: false,
  selector: 'app-sampling-equipment-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss']
})
export class SamplingEquipmentDialogComponent extends FormComponent<SamplingEquipment> implements OnInit {
  sensors: Lookup[];
  organizationLevels: OrganizationLevel[] = [];

  constructor(
    dialogRef: MatDialogRef<SamplingEquipmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
    samplingEquipmentService: SamplingEquipmentService,
    protected injector: Injector,
    private typeService: TypeService,
    private sensorService: SensorService,
    private organizationLevelService: OrganizationLevelService
  ) {
    super(dialogRef, samplingEquipmentService, data, 'Sampling Equipment', injector);
  }

  ngOnInit(): void {
    this.isFormLoading = true;
    const orgPayload = JSON.stringify({
      OperatorTypeEnum: JSON.stringify({
        Condition: OperatorTypeEnum.Equal,
        Field: nameOf<OrganizationLevel>(x => x.orgLevelType),
        Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`
      })
    });

    forkJoin({
      entity: this.getEntity(),
      sensors: this.sensorService.getAll(),
      organizationLevels: this.organizationLevelService.getAll(orgPayload),
    }).pipe(
      // the loader is cleared on every terminal outcome — including a throw inside the
      // next handler below, which would otherwise leave the dialog on an indefinite spinner.
      finalize(() => this.isFormLoading = false)
    ).subscribe({
      next: result => {
        this.entity = result.entity;
        this.sensors = result.sensors;
        this.organizationLevels = result.organizationLevels;
        this.initModels();
        this.initFields();
      },
      error: err => this.toastService.showError(err)
    });
  }

  initModels() {
    if (this.entity.dateAcquired) {
      this.entity.dateAcquired = this.dateFormatService.convertUTCDateStringToUserTimeZoneString(this.entity.dateAcquired);
    }
  }

  getNewObject(): SamplingEquipment {
    return new SamplingEquipment();
  }

  validate(): boolean {
    return true;
  }

  initFields(): void {
    const enumLookup = (value) => this.typeService.getEnumOptions(value).map(option => ({
      id: option.id, name: option.name.replace(/([A-Z])/g, ' $1').trim()
    }));
    this.fields = [
      new TextFormField({
        property: nameOf<SamplingEquipment>(x => x.modelNumber),
        name: 'Model Number',
        required: false
      }),
      new TextFormField({
        property: nameOf<SamplingEquipment>(x => x.equipmentCode),
        name: 'Equipment Code',
        required: false
      }),
      new TextFormField({
        property: nameOf<SamplingEquipment>(x => x.serialNumber),
        name: 'Serial Number',
        required: false
      }),
      new SingleSelectionFormField({
        property: nameOf<SamplingEquipment>(x => x.sampleType),
        name: 'Agent Type',
        options: enumLookup(SampleTypeEnum)
      }),
      new HierarchicalSelectionFormField({
        property: nameOf<SamplingEquipment>(x => x.organizationLevelId),
        name: 'Organization',
        options: toOrganizationTree(this.organizationLevels),
        required: true
      }),
      new TextFormField({
        property: nameOf<SamplingEquipment>(x => x.manufacturer),
        name: 'Manufacturer',
        required: false
      }),
      new DateFormField({
        property: nameOf<SamplingEquipment>(x => x.dateAcquired),
        name: 'Date Acquired',
      }),
      new SingleSelectionFormField({
        property: nameOf<SamplingEquipment>(x => x.status),
        name: 'Status',
        options: enumLookup(EquipmentStatus)
      }),
      new SingleSelectionFormField({
        property: nameOf<SamplingEquipment>(x => x.sensorId),
        name: 'Sensor Id',
        options: this.sensors, required: false
      }),
    ];
  }

  onBeforeSave(model: SamplingEquipment): void {
    if (model.dateAcquired) model.dateAcquired = this.dateFormatService.convertUserTimeZoneStringToUTCDateString(model.dateAcquired);
  }
}
